import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  CircularProgress,
  Collapse,
  Grid2,
  IconButton,
  Paper,
  useTheme,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { observer, useLocalObservable } from "mobx-react";
import PageTitle from "../../../../../views/components/common/pages/PageTitle";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import CustomTextField from "../../../../../views/components/common/textFields/TextField";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import ConfirmationPopup from "../../../../../views/components/common/pop-up/ConfirmationPopup";
import CategoryTable from "./CategoryTable";
import CategoryFormModal from "./CategoryFormModal";
import { useDebounce } from "../../../../../views/components/common/hooks/Debounce";
import { useStores } from "../../../use-stores";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import { useNotification } from "../../../../../utils/NotificationsContext";
import { CategoryStore } from "../../../stores/CategoryStore";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import {
  CATEGORY_SORT_BY_OPTIONS,
  CategoryListItem,
  GetCategoriesParams,
  getApiErrorMessage,
  normalizeCategoryListSortBy,
} from "../../../../../models/Categories/types";
import { SortType } from "../../../../../models/SortType";

const SEARCH_DEBOUNCE_MS = 500;

const SORT_BY_LIST: FilterValue[] = CATEGORY_SORT_BY_OPTIONS.map((o) => ({
  id: o.value,
  value: o.label,
}));

const SORT_DIRECTION_LIST: FilterValue[] = [
  { id: SortType.ASC, value: "Ascending" },
  { id: SortType.DESC, value: "Descending" },
];

const ACTIVE_FILTER_LIST: FilterValue[] = [
  { id: "all", value: "All" },
  { id: "1", value: "Active" },
  { id: "0", value: "Inactive" },
];

const DISPLAY_FILTER_LIST: FilterValue[] = [
  { id: "all", value: "All" },
  { id: "1", value: "Displayed" },
  { id: "0", value: "Hidden" },
];

function parseSortDirectionFromSearch(searchParams: URLSearchParams): SortType {
  const dirRaw =
    searchParams.get("sort_direction") ||
    searchParams.get("sort_order") ||
    searchParams.get("sort_type");
  const dirNorm = dirRaw?.trim().toUpperCase();
  if (!dirNorm) return SortType.ASC;
  return dirNorm === "ASC" ? SortType.ASC : SortType.DESC;
}

function parseBoolFilter(
  searchParams: URLSearchParams,
  key: string,
): boolean | undefined {
  const v = searchParams.get(key);
  if (v === "1" || v === "true") return true;
  if (v === "0" || v === "false") return false;
  return undefined;
}

function getParamsFromSearch(search: string): GetCategoriesParams {
  const searchParams = new URLSearchParams(search);
  const pageRaw = searchParams.get("page");
  const limitRaw = searchParams.get("limit");
  return {
    page_no: pageRaw ? parseInt(pageRaw, 10) || 1 : 1,
    limit: limitRaw ? parseInt(limitRaw, 10) || 30 : 30,
    sort_by: normalizeCategoryListSortBy(searchParams.get("sort_by")),
    sort_type: parseSortDirectionFromSearch(searchParams),
    search: searchParams.get("search") || searchParams.get("name") || undefined,
    active: parseBoolFilter(searchParams, "active"),
    display: parseBoolFilter(searchParams, "display"),
  };
}

const CategoryListing: React.FC = () => {
  const theme = useTheme();
  const { userStore } = useStores();
  const { showError, showSuccess } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const categoryStore = useRef(new CategoryStore()).current;

  const canEdit = userStore.hasPermission(EmployeeRoles.EDIT_CATALOG, true);

  const initialParams = getParamsFromSearch(location.search);
  const [searchTerm, setSearchTerm] = useState(initialParams.search || "");
  const debouncedSearchTerm = useDebounce(searchTerm, SEARCH_DEBOUNCE_MS);

  const listing = useLocalObservable(() => ({
    params: initialParams,
    isFiltersExpanded: true,
    isLoading: false,
    categoryModalOpen: false,
    deletePopupOpen: false,
    restorePopupOpen: false,
    actionCategory: null as CategoryListItem | null,
    actionInProgress: false,
    setParams(updater: (prev: GetCategoriesParams) => GetCategoriesParams) {
      this.params = updater(this.params);
    },
    syncFromLocationSearch(search: string) {
      const next = getParamsFromSearch(search);
      const prev = this.params;
      if (
        prev.page_no === next.page_no &&
        (prev.limit ?? 30) === (next.limit ?? 30) &&
        normalizeCategoryListSortBy(prev.sort_by) === next.sort_by &&
        (prev.sort_type ?? SortType.ASC) === (next.sort_type ?? SortType.ASC) &&
        (prev.search ?? "") === (next.search ?? "") &&
        prev.active === next.active &&
        prev.display === next.display
      ) {
        return null;
      }
      this.params = next;
      return next.search ?? "";
    },
  }));

  useEffect(() => {
    const nextSearch = debouncedSearchTerm.trim() || undefined;
    if ((listing.params.search ?? "") === (nextSearch ?? "")) return;
    listing.setParams((prev) => ({
      ...prev,
      search: nextSearch,
      page_no: 1,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  const refreshListing = () => {
    categoryStore.listCategories(listing.params).catch((err: unknown) => {
      showError("Error", getApiErrorMessage(err, "Failed to load categories."));
    });
  };

  useEffect(() => {
    let cancelled = false;
    listing.isLoading = true;
    categoryStore
      .listCategories(listing.params)
      .catch((err: unknown) => {
        if (cancelled) return;
        showError(
          "Error",
          getApiErrorMessage(err, "Failed to load categories."),
        );
      })
      .finally(() => {
        if (!cancelled) listing.isLoading = false;
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listing.params, categoryStore]);

  useLayoutEffect(() => {
    const q = new URLSearchParams();
    q.set("page", String(listing.params.page_no));
    if (listing.params.limit) q.set("limit", String(listing.params.limit));
    q.set("sort_by", normalizeCategoryListSortBy(listing.params.sort_by));
    q.set(
      "sort_direction",
      listing.params.sort_type === SortType.ASC ? "ASC" : "DESC",
    );
    if (listing.params.search) q.set("search", listing.params.search);
    if (listing.params.active !== undefined)
      q.set("active", listing.params.active ? "1" : "0");
    if (listing.params.display !== undefined)
      q.set("display", listing.params.display ? "1" : "0");
    navigate({ search: q.toString() }, { replace: true });
  }, [listing.params, navigate]);

  useEffect(() => {
    const syncedSearch = listing.syncFromLocationSearch(location.search);
    if (
      syncedSearch !== null &&
      debouncedSearchTerm === searchTerm &&
      syncedSearch !== searchTerm
    ) {
      setSearchTerm(syncedSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const applySearchNow = (raw: string) => {
    const trimmed = raw.trim();
    setSearchTerm(raw);
    listing.setParams((prev) => ({
      ...prev,
      search: trimmed || undefined,
      page_no: 1,
    }));
  };

  const handleSearchSubmit = () => {
    applySearchNow(searchTerm);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSearchSubmit();
  };

  const resetFilters = () => {
    setSearchTerm("");
    listing.params = {
      page_no: 1,
      limit: 30,
      sort_by: "name",
      sort_type: SortType.ASC,
      search: undefined,
      active: undefined,
      display: undefined,
    };
  };

  const ensureCanEdit = (actionLabel: string) => {
    if (canEdit) return true;
    showError(
      "Permission denied",
      `You don't have permission to ${actionLabel} categories.`,
    );
    return false;
  };

  const handleToggleDisplay = async (category: CategoryListItem) => {
    if (!ensureCanEdit("update")) return;
    const prev = category.display;
    const next = prev === 1 ? 0 : 1;
    categoryStore.patchListItem(category.id, { display: next });
    try {
      const result = await categoryStore.toggleCategoryDisplay(
        category.id,
        next,
      );
      categoryStore.patchListItem(category.id, { display: result.display });
    } catch (error) {
      categoryStore.patchListItem(category.id, { display: prev });
      showError(
        "Error",
        getApiErrorMessage(error, "Could not update display status."),
      );
    }
  };

  const handleDeleteConfirm = async () => {
    if (!ensureCanEdit("delete")) return;
    if (!listing.actionCategory || listing.actionInProgress) return;
    try {
      listing.actionInProgress = true;
      await categoryStore.deleteCategoryRecord(listing.actionCategory.id);
      listing.deletePopupOpen = false;
      listing.actionCategory = null;
      refreshListing();
    } catch (error) {
      showError(
        "Error",
        getApiErrorMessage(error, "Failed to delete category."),
      );
    } finally {
      listing.actionInProgress = false;
    }
  };

  const handleRestoreConfirm = async () => {
    if (!ensureCanEdit("restore")) return;
    if (!listing.actionCategory || listing.actionInProgress) return;
    try {
      listing.actionInProgress = true;
      await categoryStore.toggleCategoryActive(listing.actionCategory.id, 1);
      listing.restorePopupOpen = false;
      listing.actionCategory = null;
      refreshListing();
    } catch (error) {
      showError(
        "Error",
        getApiErrorMessage(error, "Failed to restore category."),
      );
    } finally {
      listing.actionInProgress = false;
    }
  };

  return (
    <Grid2
      size={{ xs: 12, sm: 12, md: 12, lg: 12 }}
      sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}
    >
      <Paper sx={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}>
        <Box
          sx={{
            marginTop: "0.6rem",
            paddingX: 4,
            paddingY: 2,
            boxShadow:
              "rgba(0, 0, 0, 0.25) 0px 0.0625em 0.0625em, rgba(0, 0, 0, 0.25) 0px 0.125em 0.5em, rgba(255, 255, 255, 0.1) 0px 0px 0px 1px inset",
            borderRadius: "4px",
            width: "100%",
            maxWidth: "100%",
            overflow: "hidden",
            minHeight: {
              xs: "60vh",
              md:
                typeof window !== "undefined"
                  ? window.innerHeight - 110
                  : "70vh",
            },
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            <Grid2
              container
              spacing={2}
              columns={12}
              alignItems="center"
              sx={{ paddingBottom: 2 }}
            >
              <Grid2 size={{ xs: 12, sm: "grow" }}>
                <PageTitle title="Product Categories" />
              </Grid2>
              {canEdit ? (
                <Grid2 size={{ xs: 12, sm: "auto" }}>
                  <AppButton
                    variant="primary"
                    label="Add Category"
                    onClick={() => {
                      listing.categoryModalOpen = true;
                    }}
                  />
                </Grid2>
              ) : null}
            </Grid2>

            <Paper
              variant="outlined"
              sx={{
                mb: 2,
                borderRadius: 2,
                borderColor: "divider",
                overflow: "hidden",
              }}
            >
              <Box
                onClick={() =>
                  (listing.isFiltersExpanded = !listing.isFiltersExpanded)
                }
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  px: 2,
                  py: 0.5,
                  cursor: "pointer",
                  borderBottom: listing.isFiltersExpanded
                    ? "1px solid"
                    : "none",
                  borderColor: "divider",
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <CustomTypography variant="heading7">
                  Search & Filters
                </CustomTypography>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    listing.isFiltersExpanded = !listing.isFiltersExpanded;
                  }}
                >
                  {listing.isFiltersExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>
              <Collapse
                in={listing.isFiltersExpanded}
                timeout="auto"
                unmountOnExit
              >
                <Grid2 container spacing={2} columns={12} padding={2}>
                  <Grid2 size={{ xs: 12, sm: 8, md: 8, lg: 8 }}>
                    <CustomTextField
                      width="100%"
                      placeholder="Search by name, code, or ID"
                      value={searchTerm}
                      onSearch={(value) => {
                        setSearchTerm(value || "");
                      }}
                      onChange={(e) => {
                        setSearchTerm(e.target.value || "");
                      }}
                      endAdornment={
                        <SearchIcon
                          onClick={() => handleSearchSubmit()}
                          sx={{ color: "#7C7A7F", fontSize: "20px" }}
                        />
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleKeyDown(e);
                      }}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 6, sm: 2, md: 2, lg: 2 }}>
                    <AppButton
                      variant="primary"
                      label="Search"
                      onClick={handleSearchSubmit}
                      customStyles={{ width: "100%", height: 40 }}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 6, sm: 2, md: 2, lg: 2 }}>
                    <AppButton
                      variant="transparentPrimary"
                      label="Clear"
                      onClick={resetFilters}
                      customStyles={{ width: "100%", height: 40 }}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
                    <AppSearchDropdownMulti
                      title="Sort by"
                      isMultiSelect={false}
                      local
                      list={SORT_BY_LIST}
                      value={SORT_BY_LIST.filter(
                        (o) =>
                          o.id ===
                          normalizeCategoryListSortBy(listing.params.sort_by),
                      )}
                      handleSelect={(items) => {
                        const next = items[0]?.id;
                        if (!next) return;
                        listing.setParams((p) => ({
                          ...p,
                          sort_by: normalizeCategoryListSortBy(next),
                          page_no: 1,
                        }));
                      }}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
                    <AppSearchDropdownMulti
                      title="Direction"
                      isMultiSelect={false}
                      local
                      list={SORT_DIRECTION_LIST}
                      value={SORT_DIRECTION_LIST.filter(
                        (o) =>
                          o.id === (listing.params.sort_type ?? SortType.ASC),
                      )}
                      handleSelect={(items) => {
                        const next = items[0]?.id;
                        if (!next) return;
                        listing.setParams((p) => ({
                          ...p,
                          sort_type:
                            next === SortType.DESC
                              ? SortType.DESC
                              : SortType.ASC,
                          page_no: 1,
                        }));
                      }}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
                    <AppSearchDropdownMulti
                      title="Active"
                      isMultiSelect={false}
                      local
                      list={ACTIVE_FILTER_LIST}
                      value={ACTIVE_FILTER_LIST.filter((o) => {
                        if (listing.params.active === undefined) {
                          return o.id === "all";
                        }
                        return o.id === (listing.params.active ? "1" : "0");
                      })}
                      handleSelect={(items) => {
                        const next = items[0]?.id ?? "all";
                        listing.setParams((p) => ({
                          ...p,
                          active: next === "all" ? undefined : next === "1",
                          page_no: 1,
                        }));
                      }}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
                    <AppSearchDropdownMulti
                      title="Display"
                      isMultiSelect={false}
                      local
                      list={DISPLAY_FILTER_LIST}
                      value={DISPLAY_FILTER_LIST.filter((o) => {
                        if (listing.params.display === undefined) {
                          return o.id === "all";
                        }
                        return o.id === (listing.params.display ? "1" : "0");
                      })}
                      handleSelect={(items) => {
                        const next = items[0]?.id ?? "all";
                        listing.setParams((p) => ({
                          ...p,
                          display: next === "all" ? undefined : next === "1",
                          page_no: 1,
                        }));
                      }}
                    />
                  </Grid2>
                </Grid2>
              </Collapse>
            </Paper>

            {listing.isLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: 280,
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <CategoryTable
                maxHeight={
                  typeof window !== "undefined" ? window.innerHeight - 360 : 480
                }
                categories={categoryStore.categories}
                limit={listing.params.limit ?? 30}
                page_no={listing.params.page_no}
                total_count={categoryStore.total_count}
                sortBy={normalizeCategoryListSortBy(listing.params.sort_by)}
                sortType={listing.params.sort_type ?? SortType.ASC}
                onPageChange={(_e, page) =>
                  listing.setParams((p) => ({ ...p, page_no: page }))
                }
                onRowsPerPageChange={(limit) =>
                  listing.setParams((p) => ({
                    ...p,
                    limit,
                    page_no: 1,
                  }))
                }
                onSortChange={(column, sortType) =>
                  listing.setParams((p) => ({
                    ...p,
                    sort_by: normalizeCategoryListSortBy(column),
                    sort_type: sortType,
                    page_no: 1,
                  }))
                }
                onToggleDisplay={handleToggleDisplay}
                onDelete={(category) => {
                  if (!ensureCanEdit("delete")) return;
                  listing.actionCategory = category;
                  listing.deletePopupOpen = true;
                }}
                onRestore={(category) => {
                  if (!ensureCanEdit("restore")) return;
                  listing.actionCategory = category;
                  listing.restorePopupOpen = true;
                }}
              />
            )}
          </Box>
        </Box>
      </Paper>

      <CategoryFormModal
        open={listing.categoryModalOpen}
        store={categoryStore}
        onClose={() => {
          listing.categoryModalOpen = false;
        }}
        onSuccess={(categoryId) => {
          if (categoryId) {
            navigate(`/categories/${categoryId}`);
            return;
          }
          refreshListing();
        }}
      />

      {listing.deletePopupOpen && listing.actionCategory ? (
        <ConfirmationPopup
          id={listing.actionCategory.id}
          open={listing.deletePopupOpen}
          title="Delete Category"
          content={`Soft-delete "${listing.actionCategory.name}"? You can restore it later from the listing.`}
          onConfirm={() => {
            void handleDeleteConfirm();
          }}
          onCancel={() => {
            listing.deletePopupOpen = false;
            listing.actionCategory = null;
          }}
        />
      ) : null}

      {listing.restorePopupOpen && listing.actionCategory ? (
        <ConfirmationPopup
          id={listing.actionCategory.id}
          open={listing.restorePopupOpen}
          title="Restore Category"
          content={`Restore "${listing.actionCategory.name}" and mark it active again?`}
          onConfirm={() => {
            void handleRestoreConfirm();
          }}
          onCancel={() => {
            listing.restorePopupOpen = false;
            listing.actionCategory = null;
          }}
        />
      ) : null}
    </Grid2>
  );
};

export default observer(CategoryListing);
